import { useEffect, useId, useRef, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { FLOATING_ACTION_BOTTOM_CLASS } from '../../constants/floatingActions.js';
import {
  getWhatsAppUrl,
  WHATSAPP_PHONE,
  WHATSAPP_TOPICS,
} from '../../constants/whatsappContact.js';
import WorldCupPollaFloatingButton from './WorldCupPollaFloatingButton.jsx';

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function WhatsAppFloatingButton({ showPolla = false }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!WHATSAPP_PHONE || WHATSAPP_TOPICS.length === 0) return null;

  function openWhatsApp(message) {
    const url = getWhatsAppUrl(message);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  }

  return (
    <div
      ref={rootRef}
      className={`fixed ${FLOATING_ACTION_BOTTOM_CLASS} right-4 z-50 flex flex-col items-end gap-3 sm:right-8 lg:right-10`}
    >
      {open && (
        <div
          id={menuId}
          role="dialog"
          aria-label="Opciones de contacto por WhatsApp"
          className="w-[min(100vw-2rem,22rem)] overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_16px_40px_rgba(0,0,0,0.18)] ring-1 ring-black/5"
        >
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3.5">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-[#25D366]" aria-hidden />
              <p className="text-base font-semibold text-slate-800">¿En qué te ayudamos?</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-200/80 hover:text-slate-700"
              aria-label="Cerrar menú"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <ul className="py-1">
            {WHATSAPP_TOPICS.map((topic) => (
              <li key={topic.id}>
                <button
                  type="button"
                  onClick={() => openWhatsApp(topic.message)}
                  className="flex w-full items-start gap-3 px-4 py-3.5 text-left text-base text-slate-700 transition hover:bg-[#25D366]/10 hover:text-[#0d2b7f] focus-visible:bg-[#25D366]/10 focus-visible:outline-none"
                >
                  <WhatsAppIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#25D366]" />
                  <span className="text-base font-medium leading-snug">{topic.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showPolla ? <WorldCupPollaFloatingButton /> : null}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="dialog"
        className="group flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_8px_28px_rgba(37,211,102,0.45)] ring-2 ring-black/15 transition duration-200 hover:scale-105 hover:bg-[#20bd5a] hover:shadow-[0_12px_32px_rgba(37,211,102,0.55)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#25D366]"
        aria-label={open ? 'Cerrar opciones de WhatsApp' : 'Abrir opciones de WhatsApp'}
        title="WhatsApp"
      >
        {open ? (
          <X className="h-7 w-7" aria-hidden />
        ) : (
          <WhatsAppIcon className="h-7 w-7 transition group-hover:scale-110" />
        )}
      </button>
    </div>
  );
}
